import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@material-ui/core";
import { useDispatch } from "react-redux";
import { actualizarOrdenDeRegistro } from "../../../../../data-store/actions/registry-actions";
import { createEventAttribute } from "../../../../../data-store/actions/events-actions";
import { showSnackbar } from "../../../../../data-store/actions/organizers-actions";
import { changesRegistryFunc } from "../../../../../firebase/firebasejs";
import {
  normalizarOrder,
  calcularTotal,
  construirItemAjuste,
  construirDescripcionCambios,
} from "./orderDetailLogic";

// Puede editar la orden (agregar/quitar ítems, cantidad, precio, campos de
// producto) cualquier usuario master/MasterOrganizador del evento — ver
// registryAutorizedUsers, mismo criterio ya usado en RegistryContainerUI.jsx.
function calcularPuedeEditar(registryAutorizedUsers, userID) {
  const perfil =
    registryAutorizedUsers && registryAutorizedUsers[userID] && registryAutorizedUsers[userID].perfil;
  if (!perfil) return false;
  return perfil.localeCompare("master") === 0 || perfil.localeCompare("MasterOrganizador") === 0;
}

const OrderDetail = ({
  ParticipantDetail,
  ParticipantDetailKey,
  eventDetail,
  eventSubEventsRegistry,
  registryAutorizedUsers,
  userID,
  atleteAccount,
}) => {
  const dispatch = useDispatch();
  const monedaBase = ParticipantDetail.monedaBase;
  const puedeEditar = calcularPuedeEditar(registryAutorizedUsers, userID);

  const [editando, setEditando] = useState(false);
  const [items, setItems] = useState([]);
  const [itemsOriginal, setItemsOriginal] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [productoAAgregar, setProductoAAgregar] = useState("");

  // Diálogo de ajuste: se abre cuando, al guardar, el total editado ya no
  // coincide con el que había antes de esta sesión de edición.
  const [dialogoAjusteAbierto, setDialogoAjusteAbierto] = useState(false);
  const [diferenciaPendiente, setDiferenciaPendiente] = useState(0);
  const [itemsPendientes, setItemsPendientes] = useState(null);
  const [notaAjuste, setNotaAjuste] = useState("");

  useEffect(() => {
    const normalizado = normalizarOrder(ParticipantDetail.order).map((item, idx) => ({
      ...item,
      _uid: idx,
    }));
    setItems(normalizado);
    setItemsOriginal(normalizado);
  }, [ParticipantDetail.order]);

  // Catálogo de productos adicionales disponibles para la modalidad de este
  // atleta — mismo origen que ya usa ListAditionalProduct.jsx/CardProduct.jsx
  // al momento de la inscripción original.
  const productosDisponibles = useMemo(() => {
    const config =
      eventSubEventsRegistry && eventSubEventsRegistry["modalidad_" + ParticipantDetail.modalidad];
    const productos = (config && config.configModalidad && config.configModalidad.productosAdicionales) || {};
    return Object.values(productos);
  }, [eventSubEventsRegistry, ParticipantDetail.modalidad]);

  const productosPorCodigo = useMemo(() => {
    const map = {};
    productosDisponibles.forEach((p) => {
      map[p.codigoProducto] = p;
    });
    return map;
  }, [productosDisponibles]);

  function productoDelItem(item) {
    if (!item.code || !item.code.startsWith("producto-")) return null;
    const codigoProducto = item.code.replace("producto-", "");
    return productosPorCodigo[codigoProducto] || null;
  }

  function handleQuantityChange(uid, value) {
    setItems((prev) =>
      prev.map((it) => (it._uid === uid ? { ...it, quantity: parseInt(value, 10) || 0 } : it))
    );
  }

  function handlePriceChange(uid, value) {
    setItems((prev) =>
      prev.map((it) => (it._uid === uid ? { ...it, unit_price: parseFloat(value) || 0 } : it))
    );
  }

  // El texto de la descripción del ítem se arma igual que al momento de la
  // inscripción original (ver CardProduct.jsx: descripción del producto +
  // " - clave : valor" por cada campo), para que un producto editado acá se
  // vea igual que uno agregado durante el registro normal.
  function construirDescripcionProducto(producto, extraData) {
    let extraDataString = "";
    for (var campoKey in extraData) {
      extraDataString += " - " + campoKey + " : " + extraData[campoKey];
    }
    return producto.descripcionProducto + " " + extraDataString;
  }

  function handleExtraDataChange(uid, idCampo, value) {
    setItems((prev) =>
      prev.map((it) => {
        if (it._uid !== uid) return it;
        const extraData = { ...(it.extraData || {}), [idCampo]: value };
        const producto = productoDelItem(it);
        return {
          ...it,
          extraData,
          description: producto ? construirDescripcionProducto(producto, extraData) : it.description,
        };
      })
    );
  }

  function handleRemoveItem(uid) {
    setItems((prev) => prev.filter((it) => it._uid !== uid));
  }

  function handleAddProducto() {
    const producto = productosPorCodigo[productoAAgregar];
    if (!producto) return;
    const nuevo = {
      _uid: "nuevo_" + Date.now(),
      code: "producto-" + producto.codigoProducto,
      name: producto.nombreProducto,
      description: producto.descripcionProducto,
      quantity: 1,
      unit_price: Math.round(producto.precioProducto * 100) / 100,
      precioKey: "",
      extraData: {},
    };
    setItems((prev) => [...prev, nuevo]);
    setProductoAAgregar("");
  }

  function cancelarEdicion() {
    setItems(itemsOriginal);
    setEditando(false);
  }

  function iniciarGuardado() {
    const totalInicial = calcularTotal(itemsOriginal);
    const totalEditado = calcularTotal(items);
    const diferencia = Math.round((totalInicial - totalEditado) * 100) / 100;

    if (diferencia !== 0) {
      setDiferenciaPendiente(diferencia);
      setItemsPendientes(items);
      setNotaAjuste("");
      setDialogoAjusteAbierto(true);
    } else {
      guardarFinal(items);
    }
  }

  function confirmarAjuste() {
    if (!notaAjuste.trim()) return;
    const itemAjuste = {
      ...construirItemAjuste(diferenciaPendiente, notaAjuste.trim()),
      _uid: "ajuste_" + Date.now(),
    };
    setDialogoAjusteAbierto(false);
    guardarFinal([...itemsPendientes, itemAjuste]);
  }

  // Para que la bitácora diga "Talla de playera: M → L" en vez de
  // "talladeplayera: M → L" — busca la definición del campo dentro del
  // formularioProducto del ítem para resolver etiqueta bonita y, si es un
  // Select, el texto de la opción elegida en vez del valor crudo.
  function buscarDefinicionCampo(item, idCampo) {
    const producto = productoDelItem(item);
    const campos = producto && producto.formularioProducto;
    if (!campos) return null;
    return Object.values(campos).find((c) => c.iDCampo === idCampo) || null;
  }

  function resolverEtiquetaCampo(item, idCampo) {
    const campo = buscarDefinicionCampo(item, idCampo);
    return (campo && campo.etiqueta) || idCampo;
  }

  function resolverValorCampo(item, idCampo, valorCrudo) {
    const campo = buscarDefinicionCampo(item, idCampo);
    if (campo && campo.tipoCampo === "Select" && Array.isArray(campo.valor)) {
      const opcion = campo.valor.find((o) => o.valor === valorCrudo);
      if (opcion) return opcion.texto;
    }
    return valorCrudo;
  }

  function guardarFinal(itemsFinales) {
    setGuardando(true);
    const descripcion = construirDescripcionCambios(itemsOriginal, itemsFinales, {
      resolverEtiqueta: resolverEtiquetaCampo,
      resolverValor: resolverValorCampo,
    });
    const ordenParaGuardar = itemsFinales.map(({ _uid, ...resto }) => resto);
    const eventoIndex = "evento_" + eventDetail.iDEvento;

    // 22 = "Edición de orden de compra" (ver tipoChangesJson en
    // ChangesDetail.jsx, donde se agregó esta etiqueta).
    const changesRegistry = changesRegistryFunc(
      ParticipantDetailKey,
      ParticipantDetail,
      userID,
      atleteAccount,
      22,
      descripcion
    );

    actualizarOrdenDeRegistro(eventoIndex, ParticipantDetailKey, ordenParaGuardar)
      .then(() =>
        dispatch(
          createEventAttribute(
            eventoIndex,
            "inscritos/listadoInscritos/" + ParticipantDetailKey + "/changesRegistry/",
            changesRegistry
          )
        )
      )
      .then(() => {
        const normalizado = ordenParaGuardar.map((item, idx) => ({ ...item, _uid: idx }));
        setItemsOriginal(normalizado);
        setItems(normalizado);
        setEditando(false);
        dispatch(showSnackbar(true, "Orden actualizada correctamente", 5000));
      })
      .catch(() => {
        dispatch(showSnackbar(true, "Error al guardar los cambios de la orden", 5000));
      })
      .finally(() => setGuardando(false));
  }

  let totalActual = 0;

  const rows = items.map((item) => {
    const producto = editando ? productoDelItem(item) : null;
    const campos = (producto && producto.formularioProducto) || null;
    // La inscripción y la comisión en línea son un cargo único por registro
    // — no tiene sentido comprar "2 inscripciones" en el mismo ítem, así que
    // por ahora solo se permite ajustar su precio, no la cantidad.
    const cantidadBloqueada = item.code === "insc" || item.code === "comision";
    const total = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
    totalActual += total;

    return (
      <TableRow key={item._uid}>
        <TableCell>{item.name}</TableCell>
        <TableCell>{item.description}</TableCell>
        <TableCell>
          {editando && !cantidadBloqueada ? (
            <TextField
              type="number"
              value={item.quantity}
              onChange={(e) => handleQuantityChange(item._uid, e.target.value)}
              margin="dense"
              inputProps={{ min: 0 }}
              style={{ width: 80 }}
            />
          ) : (
            item.quantity
          )}
        </TableCell>
        <TableCell>
          {editando ? (
            <TextField
              type="number"
              value={item.unit_price}
              onChange={(e) => handlePriceChange(item._uid, e.target.value)}
              margin="dense"
              style={{ width: 100 }}
            />
          ) : (
            item.unit_price
          )}
        </TableCell>
        <TableCell>
          {total} {monedaBase.codigo}
        </TableCell>
        {editando && (
          <TableCell>
            {campos &&
              Object.values(campos).map((campo) => (
                <Box key={campo.iDCampo} mb={1}>
                  {campo.tipoCampo === "Select" ? (
                    <Select
                      value={(item.extraData && item.extraData[campo.iDCampo]) || ""}
                      onChange={(e) => handleExtraDataChange(item._uid, campo.iDCampo, e.target.value)}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="">
                        <em>{campo.etiqueta || campo.iDCampo}</em>
                      </MenuItem>
                      {(campo.valor || [])
                        .filter((o) => !o.oculto)
                        .map((o) => (
                          <MenuItem key={o.valor} value={o.valor}>
                            {o.texto}
                          </MenuItem>
                        ))}
                    </Select>
                  ) : (
                    <TextField
                      label={campo.etiqueta || campo.iDCampo}
                      value={(item.extraData && item.extraData[campo.iDCampo]) || ""}
                      onChange={(e) => handleExtraDataChange(item._uid, campo.iDCampo, e.target.value)}
                      margin="dense"
                      fullWidth
                    />
                  )}
                </Box>
              ))}
          </TableCell>
        )}
        {editando && (
          <TableCell>
            <Button size="small" color="secondary" onClick={() => handleRemoveItem(item._uid)}>
              Quitar
            </Button>
          </TableCell>
        )}
      </TableRow>
    );
  });

  const esPagoEnLinea = !!(ParticipantDetail.statusPago && ParticipantDetail.statusPago.idOperacion);

  return (
    <Paper style={{ padding: 16 }}>
      <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" gutterBottom>
          Detalle de Orden
        </Typography>
        {puedeEditar && !editando && (
          <Button variant="outlined" color="primary" size="small" onClick={() => setEditando(true)}>
            Editar orden
          </Button>
        )}
      </Box>
      <Divider style={{ marginBottom: 16 }} />

      {editando && (
        <Box mb={2} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Select
            value={productoAAgregar}
            onChange={(e) => setProductoAAgregar(e.target.value)}
            displayEmpty
            style={{ minWidth: 220 }}
          >
            <MenuItem value="">
              <em>Elegir producto a agregar...</em>
            </MenuItem>
            {productosDisponibles.map((p) => (
              <MenuItem key={p.codigoProducto} value={p.codigoProducto}>
                {p.nombreProducto} — {p.precioProducto} {monedaBase.codigo}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            color="primary"
            size="small"
            // OJO: no usar !productoAAgregar — el primer producto que se crea
            // para un evento siempre queda con codigoProducto: 0 (ver
            // CreateProduct.jsx), y en JS !0 es true, así que ese producto en
            // particular dejaba el botón deshabilitado aunque sí estuviera
            // seleccionado. "" (el valor del MenuItem placeholder) es el
            // único sentinel real de "nada seleccionado".
            disabled={productoAAgregar === ""}
            onClick={handleAddProducto}
          >
            Agregar producto
          </Button>
        </Box>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Producto</TableCell>
            <TableCell>Descripción</TableCell>
            <TableCell>Cantidad</TableCell>
            <TableCell>Precio</TableCell>
            <TableCell>Total</TableCell>
            {editando && <TableCell>Campos</TableCell>}
            {editando && <TableCell>Quitar</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={3} />
            <TableCell>
              <strong>Total pagado</strong>
            </TableCell>
            <TableCell>
              <Typography id="precioFinal">
                <strong>
                  {totalActual} {monedaBase.codigo}
                </strong>
              </Typography>
            </TableCell>
            <TableCell colSpan={editando ? 2 : 0} />
          </TableRow>
        </TableFooter>
      </Table>

      {editando && (
        <Box mt={2} style={{ textAlign: "right" }}>
          <Button onClick={cancelarEdicion} style={{ marginRight: 8 }} disabled={guardando}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={iniciarGuardado} disabled={guardando}>
            {guardando ? "Guardando..." : "Guardar cambios"}
          </Button>
        </Box>
      )}

      <Dialog open={dialogoAjusteAbierto} onClose={() => setDialogoAjusteAbierto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>El total de la orden cambió</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Con estos cambios, el total pasa de{" "}
            <strong>
              {calcularTotal(itemsOriginal)} {monedaBase.codigo}
            </strong>{" "}
            a{" "}
            <strong>
              {calcularTotal(itemsPendientes || [])} {monedaBase.codigo}
            </strong>{" "}
            — una diferencia de{" "}
            <strong>
              {diferenciaPendiente > 0 ? "+" : ""}
              {-diferenciaPendiente} {monedaBase.codigo}
            </strong>
            .
          </Typography>
          {esPagoEnLinea && (
            <Typography variant="body2" color="error" gutterBottom>
              Este pago fue en línea — la diferencia no se cobra ni se reembolsa automáticamente con la
              pasarela. Describe abajo qué se hizo con ella (ej. "se reembolsó en efectivo", "se cobró la
              diferencia por transferencia").
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Para que la orden siga sumando el mismo total de siempre ({calcularTotal(itemsOriginal)}{" "}
            {monedaBase.codigo}), se va a agregar un ítem de ajuste con esta nota.
          </Typography>
          <TextField
            label="¿Qué pasó con la diferencia?"
            value={notaAjuste}
            onChange={(e) => setNotaAjuste(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoAjusteAbierto(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!notaAjuste.trim() || guardando}
            onClick={confirmarAjuste}
          >
            Confirmar y guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OrderDetail;
